/**
 * REST device endpoints — DOC1 §11.1.
 *
 *   POST /devices/detect                       — type lookup by serial
 *   POST /devices/register/lighting            — claim an M8 device with secret key
 *   POST /devices/register/lift/start          — begin lift claim (OTP to registered mobile)
 *   POST /devices/register/lift/verify         — finish lift claim with OTP
 *   POST /devices/register/lift/override       — claim lift using Secret Key (override path)
 *   GET  /devices                              — list authenticated user's devices
 *   PATCH /devices/{id}                        — rename / relocate a device
 *   PATCH /devices/{id}/wired-sensors/{n}      — one wired sensor's config
 *   PUT  /devices/{id}/image                   — replace the device area photo
 *   DEL  /devices/{id}/image                   — clear the device area photo
 *   GET  /devices/{id}/image                   — fetch area photo bytes
 */
import { z } from 'zod';
import { UuidSchema } from '../common/id.js';
import { E164MobileSchema } from '../common/mobile.js';
import { Iso8601Schema } from '../common/timestamp.js';
import { SensorSlotSchema } from '../mqtt/sensors.js';
export const DeviceTypeSchema = z.enum(['lift', 'ceiling', 'stair']);
export const ControllerFamilySchema = z.enum(['m8', 'cop-lop', 'avira']);
export const PresenceSchema = z.enum(['online', 'offline']);
export const DeviceRoleSchema = z.enum(['owner', 'shared']);
/** Shared device summary — reused by sharing and admin endpoints. */
export const DeviceSchema = z.object({
    id: UuidSchema,
    /** The controller's hardware serial — used e.g. to tell a paired sensor
     *  which device's telemetry topic to publish under during provisioning. */
    serialNumber: z.string(),
    type: DeviceTypeSchema,
    name: z.string().nullable(),
    location: z.string().nullable(),
    presence: PresenceSchema,
    lastSeenAt: Iso8601Schema.nullable(),
    role: DeviceRoleSchema,
    /** True while the device is held provisionally by whoever registered it
     *  (M8/Ceiling only) — no Main User has been assigned yet. The holder keeps
     *  control/configure so an installer can test the install, and the app
     *  surfaces the "Make Myself / Another Person Main User" options until a
     *  POST /devices/main-user clears it. Optional for older backends. */
    ownerPending: z.boolean().optional(),
    /** True once a device_config row exists (devices.current_config_id is set)
     *  — the durable, server-side signal for "has this device been set up?",
     *  as opposed to the mobile app's old local-only setupComplete flag. */
    configured: z.boolean(),
    /** Controller channel count (24 or 32), as reported by the hardware at
     *  claim time. Null for devices claimed before this was captured. */
    channelCount: z.number().int().positive().nullable(),
    /** True once a device_sensors row exists for this device — LDR only
     *  actually does anything with a real sensor attached. */
    hasSensor: z.boolean(),
    /** Relative API path to fetch the area photo bytes (auth required), or null.
     *  Same convention as `profileImageUrl` — not a public/CDN URL, the client
     *  must send its bearer token. */
    areaImageUrl: z.string().nullable(),
    /** Last area-photo upload time, ISO-8601, or null. Cache-busting key. */
    areaImageUpdatedAt: Iso8601Schema.nullable(),
    // ── Live control state (stair/ceiling) ───────────────────────────────────
    //
    // The controller's CURRENT settings, shared by every phone that can see the
    // device. Before these existed each phone kept a private copy seeded from
    // its own defaults, so two phones on one controller drifted apart and never
    // re-converged. Null means "nobody has set this yet" — a client should fall
    // back to its own default rather than render a guess.
    //
    // Lift is excluded on purpose: its live state has its own hardware-reported
    // channel (GET /devices/:id/lift-status + the device_status WS push).
    /** Power state, independent of [brightness] — set_power is its own command,
     *  so "off" never has to be encoded as brightness 0. */
    isOn: z.boolean().nullable(),
    brightness: z.number().int().min(0).max(100).nullable(),
    /** Ceiling universal-brightness override latch — true while the device-wide
     *  level overrides every switch's own brightness. Null for other products
     *  and for devices that never reported/commanded it. */
    brightnessOverride: z.boolean().nullable().optional(),
    mode: z.string().nullable(),
    pattern: z.string().nullable(),
    ldrEnabled: z.boolean().nullable(),
    /** Ceiling per-switch state keyed by the switch name set during Setup — a
     *  ceiling controller drives independently-switchable groups, which one
     *  device-level on/off cannot describe. */
    switches: z
        .record(z.object({
        isOn: z.boolean().optional(),
        brightness: z.number().int().min(0).max(100).optional(),
    }))
        .nullable(),
    /** Config version last applied to the device. A client seeing this change
     *  knows another phone saved Setup and can re-read GET /devices/:id/config. */
    appliedConfigVersion: z.number().int().positive().nullable(),
    /** 'commanded' = a phone asked for this state and it was accepted;
     *  'device' = the controller itself reported it. Null when never set. */
    runtimeSource: z.enum(['commanded', 'device']).nullable(),
    /** When the control state above last changed, ISO-8601, or null. */
    runtimeUpdatedAt: Iso8601Schema.nullable(),
});
// ── PUT/DELETE/GET /devices/{id}/image — area photo ─────────────────────────
//
//   PUT    /devices/{id}/image  → { device }   (replace, needs 'configure')
//   DELETE /devices/{id}/image  → { device }   (clear,   needs 'configure')
//   GET    /devices/{id}/image  → image bytes  (needs 'control')
//
// Stored inline on `devices` (bytea), mirroring the avatar flow on `users`.
export const DeviceImageUploadBody = z.object({
    base64: z.string().min(1),
    filename: z.string().min(1).max(255).optional(),
    mimeType: z.enum(['image/jpeg', 'image/png', 'image/webp']),
});
export const DeviceImageResponse = z.object({ device: DeviceSchema });
// ── POST /devices/{id}/sensor ───────────────────────────────────────────────
// Sensor primitives moved to ../common/sensors.ts (shared with mqtt/status.ts
// for the wired-sensor report). Re-exported here so existing imports keep
// working.
export { SensorDirectionSchema, SensorTypeSchema, SENSOR_RANGE_BOUNDS, } from '../common/sensors.js';
import { SensorDirectionSchema, SensorTypeSchema, WiredSensorReportSchema, WiredSensorSettingSchema, } from '../common/sensors.js';
/** Widest span across all sensor types — the validation bound for a stored
 *  range. The per-type bound is enforced against [SENSOR_RANGE_BOUNDS] once the
 *  sensor's type is known. The floor is 0 because a type with no range concept
 *  (pir, switch — `maxCm: 0`) stores 0. */
const RANGE_FLOOR_CM = 0;
const RANGE_CEILING_CM = 1000;
/** One configured motion sensor paired to a device. */
export const SensorConfigSchema = z.object({
    id: UuidSchema,
    serial: z.string().min(1).max(64),
    /** User-set name, e.g. "Bottom of stairs". */
    name: z.string().min(1).max(60),
    rangeCm: z.number().int().min(RANGE_FLOOR_CM).max(RANGE_CEILING_CM),
    /** Which pattern this sensor triggers on motion (a built-in name or a custom
     *  pattern the user created in stair setup). */
    pattern: z.string().min(1).max(64),
    direction: SensorDirectionSchema,
    sensorType: SensorTypeSchema,
    /** True when the hardware reports a measured distance, not just presence —
     *  drives whether the app shows a live distance readout. */
    reportsDistance: z.boolean(),
    /** Usable range span for this sensor's hardware, in cm. Both 0 for a type
     *  with no range concept (pir, switch) — the app renders no range control. */
    rangeMinCm: z.number().int().nonnegative(),
    rangeMaxCm: z.number().int().nonnegative(),
    /** Latest reading: true = detected, false = clear, null = never reported. */
    motion: z.boolean().nullable(),
    /** True once the sensor reported within the freshness window. */
    online: z.boolean(),
    /** ISO-8601 timestamp of the most recent reading, or null. */
    lastReadingAt: Iso8601Schema.nullable(),
});
// ── GET /devices/{id}/sensors ───────────────────────────────────────────────
/** One WIRED sensor as the endpoint returns it — the controller's latest
 *  report (name/type/motion, from `device_runtime_state.reported_sensors`)
 *  merged by `id` with the saved config (`device_config.settings.sensors`:
 *  rangeCm/direction), decorated with the hardware bounds so the app
 *  never hardcodes them. An entry present only in config (sensor currently
 *  unreported/disconnected) still returns, with `lastReportedAt: null`. */
export const WiredSensorMergedSchema = WiredSensorReportSchema.extend({
    reportsDistance: z.boolean(),
    /** Both 0 for a type with no range concept (pir, switch). */
    rangeMinCm: z.number().int().nonnegative(),
    rangeMaxCm: z.number().int().nonnegative(),
    /** When the controller last reported this sensor, or null if it only exists
     *  in saved config (e.g. unplugged since). */
    lastReportedAt: Iso8601Schema.nullable(),
});
/** One configured ceiling slot as the endpoint returns it — the controller's
 *  reported inventory (mqtt/sensors.ts SensorSlotSchema) plus the name the
 *  user gave it in Setup, or null while the slot is reported but unconfigured.
 *  That pairing is the whole point: it lets Setup list every physical slot and
 *  show which are already in use. */
export const CeilingSlotMergedSchema = SensorSlotSchema.extend({
    /** The switch name bound to this slot (`device_config.settings.switches[]`),
     *  or null when nothing is assigned to it yet. */
    switchName: z.string().min(1).max(64).nullable(),
    /** When the controller last reported its inventory, or null if it never has. */
    lastReportedAt: Iso8601Schema.nullable(),
});
export const SensorListResponse = z.object({
    /** LEGACY external Wi-Fi sensor boards (`device_sensors` table). */
    sensors: z.array(SensorConfigSchema),
    /** WIRED sensors reported by the controller itself. Optional so older
     *  backends keep validating. */
    wired: z.array(WiredSensorMergedSchema).optional(),
    /** CEILING slots the controller reported, merged with whatever Setup has
     *  bound to them. Only ever populated for a ceiling device. */
    slots: z.array(CeilingSlotMergedSchema).optional(),
});
// ── POST /devices/{id}/sensors ──────────────────────────────────────────────
export const CreateSensorBody = z.object({
    serial: z.string().min(1).max(64),
    name: z.string().min(1).max(60),
    rangeCm: z.number().int().min(RANGE_FLOOR_CM).max(RANGE_CEILING_CM),
    pattern: z.string().min(1).max(64),
    direction: SensorDirectionSchema,
    /** Defaults to 'tof' server-side (the original detection board). Sent
     *  explicitly when the app knows the hardware — e.g. 'ultrasonic'. */
    sensorType: SensorTypeSchema.optional(),
});
// ── PATCH /devices/{id}/sensors/{sensorId} ──────────────────────────────────
export const UpdateSensorBody = z.object({
    name: z.string().min(1).max(60).optional(),
    rangeCm: z.number().int().min(RANGE_FLOOR_CM).max(RANGE_CEILING_CM).optional(),
    pattern: z.string().min(1).max(64).optional(),
    direction: SensorDirectionSchema.optional(),
});
// ── PATCH /devices/{id}/wired-sensors/{sensorId} ────────────────────────────
/**
 * Update ONE wired sensor's config, by controller input index.
 *
 * The legacy `PATCH /devices/{id}/sensors/{sensorId}` above is a different
 * endpoint entirely: it edits a row in `device_sensors` (external Wi-Fi sensor
 * boards, UUID id). A wired sensor has no row of its own — it is an entry in
 * `device_config.settings.sensors`, keyed by the controller's input index.
 *
 * Why this exists: without it, changing any single field meant the app had to
 * GET the whole config, merge, and PUT it back, so a per-field edit was a full
 * setup revision — and every unrelated field on the entry had to satisfy
 * `WiredSensorSettingSchema` before an auto-calibrate toggle could be saved.
 * The server does the read-merge-write under the same row lock and optimistic
 * version check `PUT /devices/{id}/config` uses, and it resolves `type` itself
 * (from the existing entry, else the controller's report) so the caller never
 * has to echo hardware facts back.
 *
 * At least one field must be present — an empty body is a 400, not a no-op
 * version bump.
 *
 * There is no `pattern` field: a per-sensor trigger pattern is no longer part
 * of the feature. A client still sending one is not rejected for it — zod
 * strips the unknown key — but a body carrying ONLY `pattern` has nothing left
 * to write and fails the "at least one field" check, which is the honest
 * answer rather than a silent version bump.
 */
export const UpdateWiredSensorBody = z
    .object({
    /** Range-checked against this sensor's own hardware bounds server-side. */
    rangeCm: z.number().int().positive().optional(),
    direction: SensorDirectionSchema.optional(),
    autoCalibrate: z.boolean().optional(),
    /** Optional precondition, same semantics as `PUT /config`'s: when present
     *  and stale, the write is rejected 409 CONFIG_VERSION_CONFLICT rather than
     *  silently overwriting another phone's edit. */
    expectedConfigVersion: z.number().int().optional(),
})
    .refine((b) => b.rangeCm !== undefined || b.direction !== undefined || b.autoCalibrate !== undefined, { message: 'at least one of rangeCm, direction, autoCalibrate is required' });
export const UpdateWiredSensorResponse = z.object({
    /** The new config version — feed it back as the next `expectedConfigVersion`. */
    configVersion: z.number().int(),
    /** The merged entry as it was stored, so the caller doesn't have to re-GET. */
    sensor: WiredSensorSettingSchema,
});
/** Back-compat alias — the old single-sensor register body. */
export const RegisterSensorBody = z.object({
    serial: z.string().min(1).max(64),
    rangeCm: z.number().int().min(1).max(2000),
});
// ── POST /devices/detect ───────────────────────────────────────────────────
export const DeviceDetectBody = z.object({
    serialNumber: z.string().min(1).max(64),
});
export const DeviceDetectResponse = z.object({
    type: DeviceTypeSchema,
    controllerFamily: ControllerFamilySchema,
    alreadyRegistered: z.boolean(),
});
// ── POST /devices/register/lighting ────────────────────────────────────────
export const RegisterLightingBody = z.object({
    serialNumber: z.string().min(1).max(64),
    /** Plain Secret Key — middleware hashes before any storage lookup. */
    secretKey: z.string().min(1),
    /** Controller channel count (24 or 32) as reported by the hardware itself
     *  during the hotspot /info probe — stored on device_identity so Stair
     *  Setup can cap "Ending Stair" to what the physical board actually has. */
    channels: z.number().int().min(1).max(64).optional(),
});
export const RegisterLightingResponse = z.object({
    device: DeviceSchema,
});
// ── GET / PUT /devices/{id}/local-credentials ──────────────────────────────
//
// LOCAL (LAN) control needs the device's Secret Key itself — the controller
// only takes local commands after the app sends it the string on its label.
// The backend keeps a recoverable copy beside the hash and hands it to any
// user with `control` permission on the device, so the owner's other phones
// and the members they add never have to type it.
//
// GET  → the copy, or 404 LOCAL_CREDENTIALS_UNAVAILABLE when the backend has
//        none yet (a device registered before the copy existed).
// PUT  → a phone that already holds the key hands it in. Verified against the
//        stored hash first; a wrong key is 403 INVALID_SECRET_KEY.
export const LocalCredentialsResponse = z.object({
    serialNumber: z.string(),
    /** The plain Secret Key. Keep it in secure storage on the phone only. */
    secretKey: z.string(),
});
export const LocalCredentialsBackfillBody = z.object({
    secretKey: z.string().min(1).max(128),
});
export const LocalCredentialsBackfillResponse = z.object({
    /** False when the backend's key vault is switched off — nothing was kept. */
    stored: z.boolean(),
});
// ── PATCH /devices/{id} ─────────────────────────────────────────────────────
//
// Owner-editable display details. Until this endpoint, NOTHING ever wrote
// devices.name/location — the app masked the nulls with per-kind defaults and
// the admin panel's name search could never match an app-registered device.
export const UpdateDeviceBody = z
    .object({
    name: z.string().trim().min(1).max(64),
    location: z.string().trim().min(1).max(128),
})
    .partial()
    .refine((b) => b.name !== undefined || b.location !== undefined, {
    message: 'Provide name and/or location.',
});
export const UpdateDeviceResponse = z.object({
    device: DeviceSchema,
});
// ── GET /devices/{id}/lift-status ───────────────────────────────────────────
//
// Live state of a lift, sourced from the device's own `status` MQTT publishes
// (currentFloor + door). The controller decodes its physical 7-segment display
// and reports the floor as an ORDINAL (index into the configured layout), so it
// lines up with the ordinal the app sends in a lift_call. Everything is
// nullable: a lift that has never reported, or a backend with the device plane
// disabled, returns nulls with `online: false` rather than failing.
export const LiftLiveStatusResponse = z.object({
    /** Floor ordinal the lift is currently showing, or null if unknown. */
    currentFloor: z.number().int().nullable(),
    doorOpen: z.boolean().nullable(),
    fan: z.boolean().nullable(),
    light: z.boolean().nullable(),
    /** Floor ordinal the controller is travelling to, when it initiated the journey. */
    targetFloor: z.number().int().nullable(),
    /** 'up' | 'down' | 'idle', or null when the controller didn't report it. */
    direction: z.string().nullable(),
    moving: z.boolean().nullable(),
    emergency: z.boolean().nullable(),
    maintenance: z.boolean().nullable(),
    /** Controller-reported fault code, or null when healthy/unreported. */
    fault: z.string().nullable(),
    /** True once a status arrived within the freshness window. */
    online: z.boolean(),
    /** ISO-8601 timestamp of the most recent status, or null. */
    lastReadingAt: Iso8601Schema.nullable(),
    // ── Lift v2 fields (per-feature status frames; floorId-keyed) ────────────
    // Populated from the new lift_status/light_status/fan_status/door_status
    // frames; null until the device has published them. The legacy int/bool
    // fields above keep flowing for pre-migration firmware.
    /** floorId the lift is currently at ("G", "F2"), or null if unknown. */
    currentFloorId: z.string().nullable(),
    /** RAW characters the cabin's 7-segment display shows ("G","B1","3") —
     *  rendered verbatim at the door center; null = blank or never reported. */
    displayLabel: z.string().nullable(),
    /** floorId the lift is travelling to, or null when no journey in flight. */
    targetFloorId: z.string().nullable(),
    /** floorIds with a call registered on the lift controller (cabin "called"
     *  LEDs), from the lift_status frame. Empty when none or never reported. */
    calledFloorIds: z.array(z.string()),
    /** 'IDLE' | 'WAITING_DOOR' | 'MOVING' | 'STOPPED', or null when unreported. */
    state: z.string().nullable(),
    /** 'OPEN' | 'CLOSED', or null when unreported. */
    doorState: z.string().nullable(),
    /** 'ON' | 'OFF', or null when unreported. */
    lightState: z.string().nullable(),
    /** 'ON' | 'OFF', or null when unreported. */
    fanState: z.string().nullable(),
    /** OBSERVED bell state ('ON' | 'OFF'), or null when never reported. The
     *  board only senses the bell — there is no command to ring/silence it. */
    alarmState: z.string().nullable(),
});
// ── POST /devices/register/lift/start ──────────────────────────────────────
export const RegisterLiftStartBody = z.object({
    serialNumber: z.string().min(1).max(64),
    mobileNumber: E164MobileSchema,
});
export const RegisterLiftStartResponse = z.object({
    otpSent: z.literal(true),
    resendAvailableAt: Iso8601Schema,
});
// ── POST /devices/register/lift/verify ─────────────────────────────────────
export const RegisterLiftVerifyBody = z.object({
    serialNumber: z.string().min(1).max(64),
    otp: z.string().length(6).regex(/^\d{6}$/),
});
export const RegisterLiftVerifyResponse = z.object({
    device: DeviceSchema,
});
// ── POST /devices/register/lift/override ───────────────────────────────────
export const RegisterLiftOverrideBody = z.object({
    serialNumber: z.string().min(1).max(64),
    /** Plain Secret Key — middleware hashes before any storage lookup. */
    secretKey: z.string().min(1),
    /** The new mobile to register the lift against. */
    mobileNumber: E164MobileSchema,
});
export const RegisterLiftOverrideResponse = z.object({
    device: DeviceSchema,
});
// ── POST /devices/main-user ────────────────────────────────────────────────
//
// Assigns the Main User for every device the caller currently holds
// provisionally (owner_provisional = true). Registration no longer makes the
// registrant the Main User — this is the only path that does.
//
//   { assignTo: 'self' }              → caller becomes owner + main_user
//   { assignTo: 'other', mobile }     → that mobile becomes owner + main_user
//                                       (user row stubbed now, active from
//                                       their first OTP login); the caller's
//                                       temporary access ends immediately.
export const AssignMainUserBody = z.discriminatedUnion('assignTo', [
    z.object({ assignTo: z.literal('self') }),
    z.object({ assignTo: z.literal('other'), mobile: E164MobileSchema }),
]);
export const AssignMainUserResponse = z.object({
    /** Devices whose ownership was finalised by this call. */
    deviceIds: z.array(UuidSchema),
    /** The mobile now holding Main User ownership (the caller's own for 'self'). */
    ownerMobile: E164MobileSchema,
});
// ── GET /devices ───────────────────────────────────────────────────────────
export const DeviceListResponse = z.object({
    devices: z.array(DeviceSchema),
});
// ── GET /devices/:id/warranty ──────────────────────────────────────────────
export const DeviceWarrantyResponse = z.object({
    serialNumber: z.string(),
    purchaseDate: Iso8601Schema.nullable(),
    expiryDate: Iso8601Schema.nullable(),
    status: z.enum(['active', 'expired', 'unknown']),
});
//# sourceMappingURL=devices.js.map